import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { IconGripVertical, IconCalendar, IconPencil, IconChevronDown, IconBriefcase } from '@tabler/icons-react';
import type { StatusOption } from '../workitems/utils/workItemStatusConfig';
import './JobRow.css';
import './AppointmentRow.css';

/**
 * Day-level due/overdue check: true when the appointment falls on or before
 * today (local time). Drives the amber row treatment.
 */
export const isDueOrOverdue = (appointmentDate: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const appt = new Date(appointmentDate);
  appt.setHours(0, 0, 0, 0);
  return appt <= today;
};

interface AppointmentRowProps {
  time: string;
  date: string;
  customer: string;
  /** Full "Business - Full Name" hover text shown on the customer name. */
  customerTitle?: string;
  title: string;
  /** Defaults to "Scheduled". */
  statusLabel?: string;
  /** Defaults to "No line items". */
  priceText?: string;
  /** Used to decide the amber (due-today/overdue) treatment. */
  appointmentDate: Date;
  onEdit?: () => void;
  /**
   * When set, shows a "Convert to job" action. Gated by the parent to
   * standalone (non job-linked), non-cancelled appointments only.
   */
  onConvertToJob?: () => void;
  onExpand?: () => void;
  /** Rotates the chevron to indicate the row's expansion panel is open. */
  expanded?: boolean;
  /** ── Optional drag-and-drop wiring (set by SortableRow) ── */
  /** Ref for the draggable node — goes on the appointment card. */
  setNodeRef?: (el: HTMLElement | null) => void;
  /** Ref for the drag activator — goes on the handle. */
  setActivatorNodeRef?: (el: HTMLElement | null) => void;
  /** Listeners + attributes for the drag handle. */
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  /** Right-click the drag handle (opens the reassign menu). */
  onHandleContextMenu?: (e: React.MouseEvent) => void;
  /** Transform/transition style applied to the card while sorting. */
  style?: React.CSSProperties;
  /** Omit the built-in drag handle (the parent renders it externally). */
  hideHandle?: boolean;
  /** Normalised current status (matches a StatusOption.value). */
  status?: string;
  /** When set (with statusOptions), the status pill opens a status-change menu. */
  onStatusChange?: (dvValue: string) => void;
  /** Status options for the change menu (from getStatusOptionsForType). */
  statusOptions?: StatusOption[];
}

/**
 * An appointment row. Renders as a neutral card matching JobRow, switching to
 * the amber treatment when the appointment is due today or overdue. The drag
 * handle reuses the shared `.job-row__handle` wrapper. Presentational.
 */
export const AppointmentRow: React.FC<AppointmentRowProps> = ({
  time,
  date,
  customer,
  customerTitle,
  title,
  statusLabel = 'Scheduled',
  priceText = 'No line items',
  appointmentDate,
  onEdit,
  onConvertToJob,
  onExpand,
  expanded,
  setNodeRef,
  setActivatorNodeRef,
  dragHandleProps,
  onHandleContextMenu,
  style,
  hideHandle,
  status,
  onStatusChange,
  statusOptions,
}) => {
  const amber = isDueOrOverdue(appointmentDate);
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLElement | null>(null);
  const statusClickable = Boolean(onStatusChange && statusOptions);
  return (
    <div className="job-row">
      {!hideHandle && (
        <div className="job-row__handle" ref={setActivatorNodeRef} {...dragHandleProps} onContextMenu={onHandleContextMenu}>
          <IconGripVertical size={18} stroke={1.5} />
        </div>
      )}
      <div
        className={`appt-row ${amber ? 'appt-row--amber' : 'appt-row--green'}`}
        ref={setNodeRef}
        style={onExpand ? { ...style, cursor: 'pointer' } : style}
        onClick={onExpand}
      >
        <div className="appt-row__stripe" />
        <div className="appt-row__inner">
          <div className="appt-row__meta">
            <span className="appt-row__time">{time}</span>
            <span className="appt-row__date">{date}</span>
          </div>
          <span className="appt-row__customer" title={customerTitle ?? customer}>{customer}</span>
          <span className="appt-row__title">{title}</span>
          <div className="appt-row__spacer" />
          <div className="appt-row__endblock">
            <div className="appt-row__endtop">
              <IconCalendar className="appt-row__calendar-icon" size={20} stroke={1.5} />
              <span
                className="appt-row__status"
                onClick={statusClickable ? (e) => { e.stopPropagation(); setStatusAnchorEl(e.currentTarget); } : undefined}
                style={statusClickable ? { cursor: 'pointer' } : undefined}
              >
                <span className="appt-row__status-dot" />
                {statusLabel}
              </span>
              {onConvertToJob && (
                <IconBriefcase
                  className="appt-row__convert-icon"
                  size={16}
                  stroke={1.5}
                  aria-label="Convert to job"
                  onClick={(e) => { e.stopPropagation(); onConvertToJob(); }}
                >
                  <title>Convert to job</title>
                </IconBriefcase>
              )}
              <IconPencil
                className="appt-row__edit-icon"
                size={16}
                stroke={1.5}
                onClick={(e) => { e.stopPropagation(); onEdit?.(); }}
              />
            </div>
            <div className="appt-row__price">{priceText}</div>
          </div>
          <IconChevronDown
            className="appt-row__chevron"
            size={20}
            stroke={1.5}
            style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 0.2s ease' }}
          />
        </div>
      </div>
      {statusClickable && statusOptions && (
        <Menu
          anchorEl={statusAnchorEl}
          open={Boolean(statusAnchorEl)}
          onClose={() => setStatusAnchorEl(null)}
          onClick={(e) => e.stopPropagation()}
        >
          {statusOptions.map((option) => (
            <MenuItem
              key={option.dvValue}
              selected={status === option.value}
              onClick={(e) => {
                e.stopPropagation();
                if (status !== option.value) onStatusChange?.(option.dvValue);
                setStatusAnchorEl(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
};
